import { Bell, Search, LogOut, Eye, Menu } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Pill } from "../ui";
import DateRangeFilter from "../DateRangeFilter";

export default function ContextBar({
  connected,
  auraOn,
  onToggleAura,
  onOpenSearch,
  onToggleNav,
}: {
  connected: boolean;
  auraOn: boolean;
  onToggleAura: () => void;
  onOpenSearch: () => void;
  onToggleNav: () => void;
}) {
  const { user, tenant, tenants, selectTenant, logout, activeSlug } = useAuth();

  return (
    <header className="flex flex-wrap items-center gap-2 border-b border-white/5 bg-ocean-900/30 px-3 py-2 backdrop-blur sm:h-14 sm:flex-nowrap sm:gap-4 sm:px-4 sm:py-0">
      {/* Mobile nav toggle (drawer takes over below lg) */}
      <button
        onClick={onToggleNav}
        aria-label="Open navigation"
        className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-slatey hover:text-ink lg:hidden"
      >
        <Menu size={18} />
      </button>

      {/* Org / site / region selector */}
      <div className="flex min-w-0 items-center gap-2">
        <span className="section-tag hidden sm:inline">ORG</span>
        <select
          value={activeSlug ?? tenant?.slug ?? ""}
          onChange={(e) => selectTenant(e.target.value)}
          className="min-w-0 max-w-[42vw] rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-sm text-ink outline-none focus:border-aurora-cyan/50 sm:max-w-none"
        >
          {user?.is_platform_admin && (
            <option value="__all__" className="bg-ocean-800">All tenants</option>
          )}
          {tenants.map((t) => (
            <option key={t.slug} value={t.slug} className="bg-ocean-800">
              {t.name}
            </option>
          ))}
        </select>
        {tenant?.region_label && (
          <Pill color={tenant.accent} className="hidden md:inline-flex">{tenant.region_label}</Pill>
        )}
      </div>

      {/* Global date-range filter (applies across surfaces) */}
      <DateRangeFilter />

      {/* Live sync */}
      <div className="flex items-center gap-1.5 text-xs">
        <span
          className={`h-2 w-2 rounded-full ${connected ? "bg-aurora-teal animate-pulseDot" : "bg-slate-600"}`}
        />
        <span className="mono hidden text-slatey xl:inline">{connected ? "LIVE" : "OFFLINE"}</span>
      </div>

      {/* Global search trigger (⌘K) */}
      <button
        onClick={onOpenSearch}
        className="ml-2 hidden flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/5 py-1.5 pl-3 pr-2 text-sm text-slate-500 transition hover:border-aurora-cyan/40 md:flex md:max-w-xs"
      >
        <Search size={14} />
        <span className="flex-1 text-left">Search everything…</span>
        <span className="mono rounded border border-white/10 px-1 text-[10px]">⌘K</span>
      </button>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {/* search icon on small screens (full search bar is md+) */}
        <button
          onClick={onOpenSearch}
          aria-label="Search"
          className="text-slatey hover:text-ink md:hidden"
        >
          <Search size={18} />
        </button>

        <button
          onClick={onToggleAura}
          className={`hidden items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition sm:flex ${
            auraOn
              ? "border-aurora-cyan/50 bg-aurora-cyan/10 text-aurora-cyan"
              : "border-white/10 bg-white/5 text-slatey hover:text-ink"
          }`}
        >
          <Eye size={14} /> Aura HUD
        </button>

        <button className="relative text-slatey hover:text-ink">
          <Bell size={18} />
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-accent" />
        </button>

        <div className="flex items-center gap-2 border-l border-white/10 pl-2 sm:pl-3">
          <img
            src={user?.avatar_url || "https://ui-avatars.com/api/?name=PO"}
            alt=""
            className="h-7 w-7 rounded-full border border-white/10"
          />
          <div className="hidden leading-tight lg:block">
            <div className="text-xs font-medium">{user?.first_name} {user?.last_name}</div>
            <div className="section-tag !tracking-[0.15em]">{user?.title}</div>
          </div>
          <button onClick={logout} className="ml-1 text-slatey hover:text-critical" title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}
